import React, { memo, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Checkbox, Text, FieldGroup, TimePicker, DatePicker } from '@Sage/hoi-poi-ui';

import SelectField from 'containers/components/Fields/Select';
import { getLiteral } from 'utils/getLiteral';
import { formatDate, getDateFormatPlaceholder, getPreviousAndNextDate } from 'utils/dates';
import { getError } from 'utils/Errors';

import './styles.scss';

const mergeDateAndTime = (calendarDate, timeDate) => {
    const merged = new Date(calendarDate);
    merged.setHours(timeDate.getHours(), timeDate.getMinutes(), timeDate.getSeconds(), 0);
    return merged;
};

const clampStartToNowIfNeeded = (startDate, startTime, isMaxTimeNow) => {
    if (!isMaxTimeNow || !startDate) {
        return { startDate, startTime, didClamp: false };
    }

    const now = new Date();
    const effectiveDateTime = startTime
        ? mergeDateAndTime(startDate, startTime)
        : new Date(startDate);

    if (effectiveDateTime.getTime() <= now.getTime()) {
        return { startDate, startTime, didClamp: false };
    }

    return { startDate: new Date(now), startTime: new Date(now), didClamp: true };
};

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const getEndOfToday = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end;
};

const DateTimeGroup = memo(
    ({
        data,
        defaultTime,
        errors,
        field,
        isMaxTimeNow,
        isMinTimeNow,
        label,
        maxDate,
        maxTime,
        minDate,
        minTime,
        onChange,
        readOnly,
    }) => {
        const lang = useSelector((state) => state.config?.userData?.langISOInterface);
        const [isAllDay, setIsAllDay] = useState(data?.[field.fieldIds?.isAllDay] || false);

        const [timezoneSelected, setTimezoneSelected] = useState(
            data?.[field?.fieldIds?.idTimezone] || null,
        );
        const timeFieldsRef = useRef([]);
        const dateFieldRef = useRef(null);
        const isFirstRender = useRef(true);
        const [dateValue, setDateValue] = useState(null);
        const [timeFromValue, setTimeFromValue] = useState(null);
        const [timeToValue, setTimeToValue] = useState(null);
        const oldValues = useRef({});
        const userDateFormat = getDateFormatPlaceholder();

        const effectiveMaxDate = useMemo(() => {
            if (isMaxTimeNow) return getEndOfToday();
            return maxDate;
        }, [maxDate, isMaxTimeNow]);

        const updateValues = useCallback(
            ({ syncClampToForm = false } = {}) => {
                const fieldIds = field?.fieldIds || null;
                if (!fieldIds) return null;

                let newStartDate = data?.[fieldIds?.startDate] || null;
                let newStartTime = data?.[fieldIds?.startTime] || defaultTime || null;
                const newEndTime = data?.[fieldIds?.endTime] || defaultTime || null;

                const clamped = clampStartToNowIfNeeded(newStartDate, newStartTime, isMaxTimeNow);
                newStartDate = clamped.startDate;
                newStartTime = clamped.startTime;

                if (clamped.didClamp && syncClampToForm && onChange) {
                    onChange(fieldIds.startDate)(new Date(newStartDate));
                    onChange(fieldIds.startTime)(new Date(newStartTime));
                }

                if (newStartDate && isValidDate(newStartDate)) {
                    oldValues.current[fieldIds.startDate] = newStartDate;
                    setDateValue(newStartDate);
                }
                if (newStartTime && isValidDate(newStartTime)) {
                    oldValues.current[fieldIds.startTime] = newStartTime;
                    setTimeFromValue(newStartTime);
                }
                if (newEndTime) {
                    oldValues.current[fieldIds.endTime] = newEndTime;
                    setTimeToValue(newEndTime);
                }
            },
            [field, data, defaultTime, isMaxTimeNow, onChange],
        );

        useEffect(() => {
            if (isFirstRender?.current && data) {
                isFirstRender.current = false;
                if (field?.inputs?.length) {
                    field.inputs.forEach((current) => {
                        if (current?.inputs?.length) {
                            if (timeFieldsRef?.current && !timeFieldsRef?.current?.length) {
                                timeFieldsRef.current = current.inputs;
                            }
                        } else if (current.type === 'date') {
                            dateFieldRef.current = current;
                        }
                    });
                }

                updateValues({ syncClampToForm: true });
            } else {
                const fieldIds = field.fieldIds;
                if (!fieldIds?.startDate) return;

                const startDate = data[fieldIds.startDate];
                const hadStartDate = oldValues.current[fieldIds.startDate];

                if (startDate && isValidDate(startDate) && !hadStartDate) {
                    updateValues();
                    return;
                }

                const oldStartDateMs = hadStartDate?.getTime();
                const startDateMs = isValidDate(startDate) ? startDate.getTime() : null;

                if (oldStartDateMs && startDateMs && oldStartDateMs !== startDateMs) {
                    updateValues();
                }
            }
        }, [data, updateValues, dateValue, field]);

        const handleOnChangeAllDay = useCallback(
            (id) => {
                return () => {
                    onChange && onChange(id)(!isAllDay);
                    const newIsAllDay = !isAllDay;
                    setIsAllDay(newIsAllDay);
                    const intervalChange = field?.inputAttrs?.intervalChange || 30; // in minutes

                    const date = data[dateFieldRef?.current?.id] || new Date();

                    if (newIsAllDay && timeFieldsRef?.current?.length) {
                        timeFieldsRef.current.forEach((current, index) => {
                            if (index === 0) {
                                const previous = new Date(date);
                                previous.setHours(0, 0);

                                if (dateFieldRef?.current?.id) {
                                    onChange && onChange(dateFieldRef.current.id)(previous);
                                }

                                onChange && onChange(current.id)(previous);
                            }
                            if (index === 1) {
                                const next = new Date(date);

                                next.setHours(23, 59);
                                onChange && onChange(current.id)(next);
                            }
                        });
                    } else if (
                        !newIsAllDay &&
                        timeFieldsRef?.current?.length &&
                        dateFieldRef?.current?.id
                    ) {
                        if (!dateFieldRef?.current?.id || !data[dateFieldRef?.current?.id]) return;
                        const dateNow = new Date();
                        date.setHours(dateNow.getHours(), dateNow.getMinutes());

                        const { previous, next } = getPreviousAndNextDate(intervalChange, date);
                        onChange(dateFieldRef.current.id)(previous);
                        timeFieldsRef.current.forEach((current, index) => {
                            if (index === 0) onChange && onChange(current.id)(previous);
                            if (index === 1) onChange && onChange(current.id)(next);
                        });
                    }
                    const afterOnChange = field?.inputAttrs?.afterOnChange || null;
                    afterOnChange && afterOnChange('eventDate');
                };
            },
            [onChange, isAllDay, data, field],
        );

        const updateDate = useCallback((baseDate, dateToConvert) => {
            const hours = dateToConvert.getHours();
            const minutes = dateToConvert.getMinutes();
            let baseCorrected = new Date(baseDate);
            baseCorrected.setHours(hours);
            baseCorrected.setMinutes(minutes);
            const finalDate = new Date(baseCorrected.getTime());
            return finalDate;
        }, []);

        const updateHours = useCallback((baseDate, dateToConvert) => {
            const finalDate = new Date(dateToConvert);
            const hours = baseDate.getHours();
            const minutes = baseDate.getMinutes();
            finalDate.setHours(hours, minutes, 0, 0);
            return finalDate;
        }, []);

        const getAreDatesDifferent = useCallback((dateOne, dateTwo) => {
            if (!dateOne || !dateTwo) return true;
            const year1 = dateOne.getFullYear();
            const month1 = dateOne.getMonth();
            const date1 = dateOne.getDate();
            const one = `${date1}/${month1}/${year1}`;
            const year2 = dateTwo.getFullYear();
            const month2 = dateTwo.getMonth();
            const date2 = dateTwo.getDate();
            const two = `${date2}/${month2}/${year2}`;
            if (one !== two) return true;
            return false;
        }, []);

        const getValueHasChanged = useCallback((newValue, oldValue) => {
            if (!newValue && !oldValue) return false;
            if (newValue && oldValue && newValue.getTime() === oldValue.getTime()) return false;
            return true;
        }, []);

        const handleOnChange = useCallback(
            (id) => {
                return (value) => {
                    let newValue = null;
                    if (value) newValue = new Date(value);

                    const fieldIds = field.fieldIds;

                    let shouldUpdateStartDate = false;
                    let shouldUpdateStartTime = false;
                    let shouldUpdateEndTime = false;

                    let newStartDate = null;
                    let newStartTime = null;
                    let newEndTime = null;

                    const intervalChange = field?.inputAttrs?.intervalChange || 30; // in minutes

                    if (id === fieldIds?.startDate) {
                        if (!getValueHasChanged(newValue, dateValue)) return;
                        if (newValue) {
                            if (timeFromValue) {
                                // update the startDate with the startTime time
                                shouldUpdateStartDate = true;
                                newStartDate = updateHours(timeFromValue, newValue);

                                //update the startTime with the updated date
                                shouldUpdateStartTime = true;
                                newStartTime = updateDate(newValue, timeFromValue);
                            } else {
                                // update startDate with newValue
                                shouldUpdateStartDate = true;
                                newStartDate = newValue;
                            }
                            if (timeToValue) {
                                // update the endTime with the updated date
                                shouldUpdateEndTime = true;
                                newEndTime = updateDate(newValue, timeToValue);
                            }
                        } else {
                            shouldUpdateStartDate = true;
                            newStartDate = null;
                        }
                    }

                    if (id === fieldIds?.startTime) {
                        if (!getValueHasChanged(newValue, timeFromValue)) return;
                        if (newValue) {
                            if (dateValue && getAreDatesDifferent(dateValue, newValue)) {
                                // update startTime with the updated date
                                shouldUpdateStartTime = true;
                                newStartTime = updateDate(dateValue, newValue);
                                // update startDate with the updated startTime
                                shouldUpdateStartDate = true;
                                newStartDate = newStartTime;
                                if (
                                    timeToValue &&
                                    newStartTime.getTime() >=
                                        new Date(timeToValue.setSeconds(0, 0)).getTime()
                                ) {
                                    // update endTime with updated startTime as a base that already has
                                    // updated startDate plus interval
                                    shouldUpdateEndTime = true;
                                    const newTimeMs =
                                        newStartTime.getTime() + 1000 * 60 * intervalChange;
                                    newEndTime = new Date(newTimeMs);
                                } else {
                                    // update endTime with the updated startTime that already has updated startDate
                                    shouldUpdateEndTime = true;
                                    newEndTime = updateDate(newStartTime, timeToValue);
                                }
                            } else {
                                // update startTime
                                shouldUpdateStartTime = true;
                                newStartTime = newValue;

                                // update startDate
                                shouldUpdateStartDate = true;
                                newStartDate = newValue;

                                if (
                                    timeToValue &&
                                    (newStartTime.getTime() >=
                                        new Date(timeToValue.setSeconds(0, 0)).getTime() ||
                                        getAreDatesDifferent(newStartTime, timeToValue))
                                ) {
                                    // update endTime with startTime plus interval
                                    shouldUpdateEndTime = true;
                                    const newTimeMs =
                                        newStartTime.getTime() + 1000 * 60 * intervalChange;
                                    newEndTime = new Date(newTimeMs);
                                }
                            }
                        } else {
                            shouldUpdateStartTime = true;
                            newStartTime = null;
                        }
                    }

                    if (id === fieldIds?.endTime) {
                        if (!getValueHasChanged(newValue, timeToValue)) return;
                        if (newValue) {
                            if (dateValue && getAreDatesDifferent(dateValue, newValue)) {
                                // update endTime with updatedDate
                                shouldUpdateEndTime = true;
                                newEndTime = updateDate(dateValue, newValue);

                                if (
                                    timeFromValue &&
                                    newEndTime.getTime() <=
                                        new Date(timeFromValue.setSeconds(0, 0)).getTime()
                                ) {
                                    // update startTime with endTime less interval
                                    shouldUpdateStartTime = true;
                                    const newTimeMs =
                                        newEndTime.getTime() - 1000 * 60 * intervalChange;
                                    newStartTime = new Date(newTimeMs);

                                    // update startDate with startTime that contains startDate
                                    shouldUpdateStartDate = true;
                                    newStartDate = newStartTime;
                                }
                            } else {
                                // update endTime
                                shouldUpdateEndTime = true;
                                newEndTime = newValue;

                                if (
                                    timeFromValue &&
                                    newEndTime.getTime() <=
                                        new Date(timeFromValue.setSeconds(0, 0)).getTime()
                                ) {
                                    // update startTime less interval
                                    shouldUpdateStartTime = true;
                                    const newTimeMs =
                                        newEndTime.getTime() - 1000 * 60 * intervalChange;
                                    newStartTime = new Date(newTimeMs);

                                    // update startDate with startTime
                                    shouldUpdateStartDate = true;
                                    newStartDate = newStartTime;
                                }
                            }
                        } else {
                            shouldUpdateEndTime = true;
                            newEndTime = null;
                        }
                    }

                    if (shouldUpdateStartDate) {
                        setDateValue(newStartDate);
                        onChange &&
                            fieldIds?.startDate &&
                            onChange(fieldIds.startDate)(newStartDate);
                    }
                    if (shouldUpdateStartTime) {
                        setTimeFromValue(newStartTime);
                        onChange &&
                            fieldIds?.startTime &&
                            onChange(fieldIds.startTime)(newStartTime);
                    }
                    if (shouldUpdateEndTime) {
                        setTimeToValue(newEndTime);
                        onChange && fieldIds?.endTime && onChange(fieldIds.endTime)(newEndTime);
                    }
                };
            },
            [
                dateValue,
                field,
                onChange,
                timeFromValue,
                timeToValue,
                updateDate,
                updateHours,
                getAreDatesDifferent,
                getValueHasChanged,
            ],
        );

        const handleOnChangeTimezone = useCallback(
            (id) => {
                return (value) => {
                    setTimezoneSelected(value);
                    onChange && onChange(id)(value);
                };
            },
            [onChange],
        );

        const fieldsComp = useMemo(() => {
            if (!field?.inputs?.length) return null;

            const components = [];
            let datePickerComp = null;
            let timeFieldGroupComp = null;

            let timePickerCount = 0;

            field.inputs.forEach((current, index) => {
                if (current.type === 'checkbox') {
                    const checkboxId = current.id;
                    components.push(
                        <div
                            key={`checkbox-${index}`}
                            className="fm-field-date-time-group__all-day"
                            onClick={handleOnChangeAllDay(checkboxId)}
                        >
                            <Checkbox
                                color="actionMinor"
                                checked={isAllDay}
                                onClick={(e) => e.stopPropagation()}
                                onChange={handleOnChangeAllDay(checkboxId)}
                            />
                            <div className="fm-field-date-group-new-all-day__label">
                                <Text>{getLiteral('label_calendarallday')}</Text>
                            </div>
                        </div>,
                    );
                } else if (current?.inputs?.length && !isAllDay) {
                    timePickerCount = current.inputs.length;

                    const timeFields = current.inputs.reduce((objTime, item, i) => {
                        if (!objTime?.inputs) objTime.inputs = [];
                        if (!objTime?.inputProps) objTime.inputProps = [];

                        const newTimeFromValue = timeFromValue ? new Date(timeFromValue) : null;
                        const newTimeToValue = timeToValue ? new Date(timeToValue) : null;
                        let timeValue = i === 0 ? newTimeFromValue : newTimeToValue;

                        const interval = field?.inputAttrs?.interval || 30;

                        const timeError = (errors && errors[item.id]) || null;

                        objTime.inputs.push(TimePicker);
                        objTime.inputProps.push({
                            value: timeValue,
                            onChange: handleOnChange(item.id),
                            interval: interval,
                            isClearable: false,
                            error: getError(timeError),
                            minTime: minTime,
                            maxTime: maxTime,
                            isMinTimeNow: isMinTimeNow,
                            isMaxTimeNow: isMaxTimeNow,
                            referenceDate: dateValue ? new Date(dateValue) : null,
                        });

                        return objTime;
                    }, {});

                    timeFieldGroupComp = (
                        <FieldGroup
                            className="fm-field-date-time-group__time-field"
                            key={`time-${index}`}
                            inputs={timeFields.inputs}
                            inputProps={timeFields.inputProps}
                            isFullWidth
                        />
                    );
                } else if (current.type === 'date') {
                    const newDateValue = dateValue ? new Date(dateValue) : null;
                    datePickerComp = (
                        <DatePicker
                            key={`date-${index}`}
                            label={label}
                            labelMode="horizontal"
                            value={newDateValue}
                            error={getError(errors?.[current.id])}
                            disabled={readOnly}
                            lang={lang}
                            minDate={minDate}
                            maxDate={effectiveMaxDate}
                            onChange={handleOnChange(current.id)}
                            placeholder={userDateFormat}
                            inputMaskFormat={userDateFormat}
                            formatDate={(date) => formatDate(date, 'L')}
                            isFullWidth
                        />
                    );
                } else if (current.type === 'timezone') {
                    components.push(
                        <SelectField
                            key={`timezone-${index}`}
                            value={timezoneSelected}
                            list="fm_iana_time_zone"
                            isClearable={false}
                            onChange={handleOnChangeTimezone(current.id)}
                            label=" "
                            labelMode="horizontal"
                        />,
                    );
                }
            });

            // Layout depends on number of time pickers:
            // 1 TimePicker: date + time side by side
            // 2+ TimePickers: date on top, times together below
            if (datePickerComp && timeFieldGroupComp) {
                if (timePickerCount === 1) {
                    // Date and single time side by side
                    components.unshift(
                        <div
                            key="date-time-row"
                            className="fm-field-date-time-group__date-time-row"
                        >
                            {datePickerComp}
                            {timeFieldGroupComp}
                        </div>,
                    );
                } else {
                    // Date on top, multiple times below (standalone style)
                    components.unshift(datePickerComp);
                    const standaloneTimeFieldGroupComp = React.cloneElement(timeFieldGroupComp, {
                        className: 'fm-field-date-time-group__time-field--standalone',
                    });
                    // Insert time right after date (position 1)
                    components.splice(1, 0, standaloneTimeFieldGroupComp);
                }
            } else if (datePickerComp) {
                components.unshift(datePickerComp);
            } else if (timeFieldGroupComp) {
                components.push(timeFieldGroupComp);
            }

            return components;
        }, [
            dateValue,
            errors,
            field?.inputAttrs?.interval,
            field.inputs,
            handleOnChange,
            handleOnChangeAllDay,
            handleOnChangeTimezone,
            isAllDay,
            isMaxTimeNow,
            isMinTimeNow,
            label,
            lang,
            effectiveMaxDate,
            maxTime,
            minDate,
            minTime,
            readOnly,
            userDateFormat,
            timeFromValue,
            timeToValue,
            timezoneSelected,
        ]);

        return <>{fieldsComp}</>;
    },
);

DateTimeGroup.displayName = 'DateTimeGroup';

DateTimeGroup.propTypes = {
    data: PropTypes.object,
    defaultTime: PropTypes.instanceOf(Date),
    description: PropTypes.string,
    errors: PropTypes.object,
    field: PropTypes.shape({
        fieldIds: PropTypes.shape({
            endTime: PropTypes.string,
            isAllDay: PropTypes.string,
            startDate: PropTypes.string,
            startTime: PropTypes.string,
        }),
        id: PropTypes.string,
        inputs: PropTypes.arrayOf(PropTypes.object),
        label: PropTypes.string,
        type: PropTypes.oneOf(['dateTimeGroup']),
    }),
    isBulkAction: PropTypes.bool,
    isMaxTimeNow: PropTypes.bool,
    isMinTimeNow: PropTypes.bool,
    label: PropTypes.string,
    labelMode: PropTypes.string,
    mandatory: PropTypes.bool,
    maxDate: PropTypes.instanceOf(Date),
    maxTime: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    minTime: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
};

export default DateTimeGroup;
